import { useState } from 'react';
import { completeInteractionDisposition } from '../api';
import type { InteractionDisposition, InteractionOutcome } from '../types';

const choices: Array<{ value: InteractionOutcome; label: string }> = [
  { value: 'awaiting_patient', label: 'Awaiting patient' },
  { value: 'timed_follow_up', label: 'Timed follow-up' },
  { value: 'positive_probable', label: 'Positive' },
  { value: 'appointment', label: 'Appointment discussed' },
  { value: 'lost', label: 'Lost' },
  { value: 'rejected', label: 'Rejected' },
];

const reasons = [
  ['not_seeking_treatment', 'Not seeking treatment'],
  ['wrong_or_accidental', 'Wrong / accidental enquiry'],
  ['location_mismatch', 'Location mismatch'],
  ['cost_or_timing', 'Cost / timing'],
  ['duplicate_or_handled', 'Duplicate / already handled'],
];

export default function DispositionGate({ task, onComplete }: { task: InteractionDisposition; onComplete: () => void }) {
  const [outcome, setOutcome] = useState<InteractionOutcome | ''>('');
  const [reason, setReason] = useState('');
  const [followUpAt, setFollowUpAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const needsReason = outcome === 'lost' || outcome === 'rejected';
  const valid = Boolean(outcome) && (!needsReason || Boolean(reason)) && (outcome !== 'timed_follow_up' || Boolean(followUpAt));

  async function save() {
    if (!outcome || !valid) return;
    setBusy(true); setError('');
    try {
      await completeInteractionDisposition(task.id, { outcome, reason_code: reason || null, follow_up_at: followUpAt || null });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save outcome');
    } finally { setBusy(false); }
  }

  return (
    <section className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-amber-800">Required before next action</div>
      <p className="mt-1 text-xs text-amber-900">Positive records human qualification. Reject requires a reason. Win is recorded separately with registration evidence.</p>
      {!task.lead_id && <p className="mt-1 text-xs text-amber-900">Link or create the CRM lead before Positive, follow-up or appointment. Unrelated enquiries can be rejected here.</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {choices.map((choice) => <button key={choice.value} type="button" onClick={() => setOutcome(choice.value)} className={`rounded-lg px-3 py-2 text-xs font-semibold ${outcome === choice.value ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'}`}>{choice.label}</button>)}
      </div>
      {needsReason && <select value={reason} onChange={(event) => setReason(event.target.value)} className="mt-2 w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs"><option value="">Select required reason</option>{reasons.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}
      {outcome === 'timed_follow_up' && <input type="datetime-local" value={followUpAt} onChange={(event) => setFollowUpAt(event.target.value)} className="mt-2 w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs" />}
      {error && <div className="mt-2 text-xs text-red-700">{error}</div>}
      <button type="button" disabled={!valid || busy} onClick={() => void save()} className="mt-3 w-full rounded-lg bg-slate-900 py-2 text-xs font-semibold text-white disabled:bg-slate-300">{busy ? 'Saving…' : 'Save outcome and continue'}</button>
    </section>
  );
}
